/* eslint-disable max-lines */
import { action, extendObservable, computed, runInAction, observable } from 'mobx';
import uniqueId from 'lodash.uniqueid';
import { backOff } from 'exponential-backoff';
import { StatusCodes } from 'http-status-codes';

import AwardService from 'services/AwardService';
import ControlledFeatureService from 'services/ControlledFeatureService';
import { translate } from 'services/i18nService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  FB_SET_AUTH_DATA,
  FIREBASE_DEBUG,
  FIREBASE_INITIAL_DATA,
  GET_AWARDS_RESPONSE,
  GET_FEATURED_CAM_STATS,
  GET_PLAY_TOKEN_FOR_GROUP_SHOW,
  GET_REPORT_USER_INFO,
  GET_REPORT_USER_INFO_RESPONSE,
  GET_REPORT_USER_INFO_RESPONSE_STATUS,
  GET_REPORT_USER_RESPONSE_STATUS,
  GET_SEX_QUESTIONNAIRE,
  GET_SEX_QUESTIONNAIRE_RESPONSE,
  GET_SILENCE_LIST,
  GET_SILENCE_LIST_RESPONSE,
  GET_SILENCE_USER_RESPONSE_STATUS,
  GET_STREAMING_INFO,
  GET_STREAMING_INFO_ERROR,
  GET_STREAMING_INFO_RESPONSE,
  GET_USER_DATA_RESPONSE,
  GET_USER_FRIENDS,
  GET_USER_FRIENDS_RESPONSE,
  HLS_DEBUG,
  HLS_STREAMING_URL_FAILED,
  INITIATE_PRIVATE_MESSAGE,
  INITIATE_SEND_TIP,
  PERFORMER_HAS_BEEN_FEATURED,
  POST_FEEDBACK_V2_PROFILE_RESPONSE_STATUS,
  PRIVATE_SHOW_REQUEST_SENT,
  PRIVATE_SHOW_STOP_PS,
  PRIVATE_SHOW_STOP_SPY,
  PROFILE_CONTROLLED_FEATURES,
  PROFILE_PRIVATE_MESSAGE_SENT,
  REINIT_ROOM,
  REJOIN_SPY_SHOW_ERROR,
  REPORT_USER,
  REQUEST_PLAY_TOKEN,
  ROOM_HAS_BEEN_CLOSED,
  SEND_ACCESS_FILE_URL,
  SHOW_REGISTRATION_MODAL,
  SILENCE_OR_BAN_USER_AS_MODERATOR,
  SILENCE_USER,
  TOGGLE_FAVORITES,
  TOGGLE_FRIENDS,
  UPDATE_STREAM_UUID,
  UPDATE_WEB_PRESENCE_CHAT_STATUS,
  USER_BROADCAST_STARTED,
  WISHLIST_INFO,
  WISHLIST_INFO_RESPONSE,
} from 'services/MessagingService/EventNames';
import ERRORS from 'services/MessagingService/Errors';
import ModalService from 'services/ModalService';
import NotificationService from 'services/NotificationService';
import ProfileService from 'services/ProfileService';
import SessionStorageService from 'services/SessionStorageService';
import TrackingService from 'services/TrackingService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import profileStoreInitialState from 'stores/ProfileStoreInitialState';
import UserStore from 'stores/UserStore';
import APIStates from 'enum/APIStates';
import { REQUEST_STATUS } from 'enum/helpers';
import { AVAILABLE_COUNTRIES } from 'enum/availableCountries';
import { AVAILABLE_LANGUAGES } from 'enum/availableLanguages';
import {
  ENUM_FAVOURITE_STATUS,
  ENUM_FRIEND_STATUS,
  GENDER,
} from 'enum/profile';
import routeDefinitions from 'enum/routeDefinitions';
import {
  BROADCAST_STATUS,
  NEW_SHOWS_STATE,
} from 'enum/videoPlayer';
import allUserGroups from 'enum/userGroups';
import {
  chunkArray,
  formatCountryName,
  getExtension,
} from 'utils/helpers';
import parseCatch from 'utils/parseCatch';
import updateStringWithQueryParams from 'utils/updateStringWithQueryParams';


export class ProfileStore {
  static STREAMING_SOURCE_TYPES = { HLS: 'HLS' };

  constructor() {
    this.disconnectedTimerId = null;
    extendObservable(this, profileStoreInitialState);
    // TODO: Initialization should be done at a top level to Solve all
    // dependencies, should be in constructor params
    this.messagingService = messagingService();
    this.messagingService.on(GET_STREAMING_INFO_RESPONSE, this._handleStreamingInfo);
    this.messagingService.on(FB_SET_AUTH_DATA, this._handleFbAuthData);
    this.messagingService.on(GET_USER_DATA_RESPONSE, this._handleGetUserData);
    this.messagingService.on(UPDATE_STREAM_UUID, this.handleUpdateStreamUUID);
    this.messagingService.on(GET_SILENCE_LIST_RESPONSE, this._handleSilenceList);

    this.messagingService.on(GET_REPORT_USER_INFO_RESPONSE, this._handleReportInfoResponse);
    this.messagingService.on(WISHLIST_INFO_RESPONSE, this._handleWishList);
    this.messagingService.on(GET_USER_FRIENDS_RESPONSE, this._handleFriendsList);
    this.messagingService.on(REQUEST_PLAY_TOKEN, this._handlePlayToken);
    this.messagingService.on(PRIVATE_SHOW_STOP_PS, this._handleStopPS);
    this.messagingService.on(PRIVATE_SHOW_STOP_SPY, this._handleStopSpy);
    this.messagingService.on(USER_BROADCAST_STARTED, this._handleUserBroadcastStarted);
    this.messagingService.on(FIREBASE_INITIAL_DATA, this._handleInitialFirebaseState);
    this.messagingService.on(PRIVATE_SHOW_REQUEST_SENT, this._trackPrivateShowReq);
    this.messagingService.on(GET_SEX_QUESTIONNAIRE_RESPONSE, this._handleSexQuestionnaireResponse);
    this.messagingService.on(GET_REPORT_USER_RESPONSE_STATUS, this._handleReportUserResponseStatus);
    this.messagingService.on(GET_SILENCE_USER_RESPONSE_STATUS, this._handleSilenceUserResponseStatus);
    this.messagingService.on(GET_AWARDS_RESPONSE, this._handleAwardsResponse);
    this.messagingService.on(UPDATE_WEB_PRESENCE_CHAT_STATUS, this.setIsChatConnected);
    this.messagingService.on(POST_FEEDBACK_V2_PROFILE_RESPONSE_STATUS, this.handleSendProfileFeedbackResponseStatus);
    this.messagingService.on(GET_FEATURED_CAM_STATS, this.handleFeaturedCamStats);
    this.messagingService.on(GET_REPORT_USER_INFO_RESPONSE_STATUS, this.handleReportInfoStatus);
    this.pendingStreamUUID = null;
  }

  destructor() {
    this.messagingService.off(GET_STREAMING_INFO_RESPONSE, this._handleStreamingInfo);
    this.messagingService.off(GET_SILENCE_LIST_RESPONSE, this._handleSilenceList);

    this.messagingService.off(GET_REPORT_USER_INFO_RESPONSE, this._handleReportInfoResponse);
    this.messagingService.off(UPDATE_STREAM_UUID, this.handleUpdateStreamUUID);
    this.messagingService.off(WISHLIST_INFO_RESPONSE, this._handleWishList);
    this.messagingService.off(GET_USER_FRIENDS_RESPONSE, this._handleFriendsList);
    this.messagingService.off(FB_SET_AUTH_DATA, this._handleFbAuthData);
    this.messagingService.off(GET_USER_DATA_RESPONSE, this._handleGetUserData);
    this.messagingService.off(REQUEST_PLAY_TOKEN, this._handlePlayToken);
    this.messagingService.off(PRIVATE_SHOW_STOP_PS, this._handleStopPS);
    this.messagingService.off(PRIVATE_SHOW_STOP_SPY, this._handleStopSpy);
    this.messagingService.off(USER_BROADCAST_STARTED, this._handleUserBroadcastStarted);
    this.messagingService.off(FIREBASE_INITIAL_DATA, this._handleInitialFirebaseState);
    this.messagingService.off(PRIVATE_SHOW_REQUEST_SENT, this._trackPrivateShowReq);
    this.messagingService.off(GET_SEX_QUESTIONNAIRE_RESPONSE, this._handleSexQuestionnaireResponse);
    this.messagingService.off(GET_REPORT_USER_RESPONSE_STATUS, this._handleReportUserResponseStatus);
    this.messagingService.off(GET_SILENCE_USER_RESPONSE_STATUS, this._handleSilenceUserResponseStatus);
    this.messagingService.off(GET_AWARDS_RESPONSE, this._handleAwardsResponse);
    this.messagingService.off(UPDATE_WEB_PRESENCE_CHAT_STATUS, this.setIsChatConnected);
    this.messagingService.off(POST_FEEDBACK_V2_PROFILE_RESPONSE_STATUS, this.handleSendProfileFeedbackResponseStatus);
    this.messagingService.off(GET_FEATURED_CAM_STATS, this.handleFeaturedCamStats);
    this.messagingService.off(GET_REPORT_USER_INFO_RESPONSE_STATUS, this.handleReportInfoStatus);
    clearTimeout(this.disconnectedTimerId);
  }

  @observable inCustomFullscreen = false;
  @observable fullscreen = false;

  // eslint-disable-next-line complexity
  @computed get streamingSource() {
    this.messagingService.emit(HLS_DEBUG, { method: 'profileStore::streamingSource' });

    const isFbAuthDataExists = !!this.fbAuthData;
    const isPrivateStream = isFbAuthDataExists && this.fbAuthData.privateStream;
    const insidePrivateFlow = this.broadcastStatus === BROADCAST_STATUS.ACCEPTED_INSIDE_PS ||
      this.broadcastStatus === BROADCAST_STATUS.INSIDE_GS;
    if (FirebaseStore.isStreamingSaas) {
      if (isFbAuthDataExists) {
        const privateSource = this.privateStreamingSources?.output;
        const fbAuthData = this.fbAuthData?.output;
        if (privateSource || fbAuthData) {
          const isSpying = this.broadcastStatus === BROADCAST_STATUS.SPYING;
          if (insidePrivateFlow || isSpying) {
            return {
              type: privateSource?.protocol,
              uri: privateSource?.uri,
            };
          }

          return {
            type: fbAuthData?.protocol,
            uri: fbAuthData?.uri,
          };
        }
      }
      if (isPrivateStream) {
        const uri = updateStringWithQueryParams(this.privateHls, { id: this.playToken });
        if (uri) {
          return {
            type: ProfileStore.STREAMING_SOURCE_TYPES.HLS,
            uri,
          };
        }

        return null;
      }

      if (isFbAuthDataExists && this.fbAuthData.publicHls) {
        return {
          type: ProfileStore.STREAMING_SOURCE_TYPES.HLS,
          uri: this.fbAuthData.publicHls,
        };
      }

      return null;
    }

    const { cdnURL, edgeURL } = this.streamingInfo ? this.streamingInfo : {};
    const isPrivateRoom = isFbAuthDataExists && this.fbAuthData.privateRoom;
    const isSpying = this.broadcastStatus === BROADCAST_STATUS.SPYING && this.playToken;
    const insidePSOrGS =
      (this.broadcastStatus === BROADCAST_STATUS.ACCEPTED_INSIDE_PS ||
        this.broadcastStatus === BROADCAST_STATUS.INSIDE_GS) &&
      !!this.playToken;

    let videoUrl = insidePSOrGS || isSpying || isPrivateRoom ? edgeURL : cdnURL;
    if (!videoUrl) {
      return null;
    }

    if (this.currentUser && isPrivateRoom && !(insidePSOrGS || isSpying)) {
      const { playToken, username } = this.fbAuthData;
      if (!(playToken && username)) {
        return null;
      }

      videoUrl += `?playToken=${playToken}&username=${username}&connectToken=${this.currentUser.streamConnectToken}`;
    }
    if (UserStore.user && (insidePSOrGS || isSpying)) {
      const { username } = this.fbAuthData;
      if (!username || !this.playToken) {
        return null;
      }

      videoUrl += `?playToken=${this.playToken}&username=${username}&connectToken=${UserStore.user.streamConnectToken}`;
    }
    if (videoUrl) {
      return {
        type: ProfileStore.STREAMING_SOURCE_TYPES.HLS,
        uri: videoUrl,
      };
    }

    return null;
  }

  @computed get shouldNoIndexFollow() {
    if (this.profileNoIndexFollow || (this.performerDetails && this.performerDetails.lastBroadcast === null)) {
      return true;
    }

    return false;

  }

  @action setVideoStatsVisibility = (isVideoStatsVisible) => {
    this.isVideoStatsVisible = isVideoStatsVisible;
  };

  @action handleReportInfoStatus = (status) => {
    this.reportInfoGETResponseStatus = status;
  };

  @action setBroadcastingStarted = (flag) => {
    this.broadcastingStarted = flag;
  };

  @action setPsGsLoading = (value) => {
    this.psGsLoading = value;
  };

  @action setPerformerOnline = (flag) => {
    this.performerOnline = flag;
  };

  @action setCustomFullscreen = (flag) => {
    this.inCustomFullscreen = flag;
  };

  @action setTheatreMode = (flag) => {
    this.inTheatreMode = flag;
  };

  @action setIsChatConnected = (flag) => {
    this.isChatConnected = flag;
    const isBroadcastWindow = location.pathname === '/broadcast';
    const SHOW_MODAL_DELAY = 100000;

    if (isBroadcastWindow) {
      if (flag) {
        clearTimeout(this.disconnectedTimerId);
      } else {
        this.disconnectedTimerId = setTimeout(
          () => ModalService.showModal(ModalService.MODAL_KEYS.BROADCAST_ERROR_MODAL),
          SHOW_MODAL_DELAY
        );
      }
    }
  };

  /**
   * This listener will run when broadcaster rotates stream in MBA or the broadcast is restarted
   * @param {Object} options contains new stream UUID from firebase
   */
  handleUpdateStreamUUID = ({ newStreamUUID }) => {
    this.pendingStreamUUID = newStreamUUID;
    SessionStorageService.remove('wasUserInPrivateShow');
    const isValidStreamName = (streamInfo) => {
      if (!newStreamUUID) {
        return true;
      }
      const cdnURL = streamInfo && streamInfo.cdnURL ? streamInfo.cdnURL.toLowerCase() : '<none>';
      const result = cdnURL.indexOf(newStreamUUID.toLowerCase()) >= 0;
      this.messagingService.emit(HLS_DEBUG, {
        method: 'profileStore::handleUpdateStreamUUID',
        type: 'validate',
        expected: newStreamUUID,
        got: cdnURL,
        valid: result,
      });

      return result;
    };
    this.messagingService.emit(HLS_DEBUG, { method: 'profileStore::handleUpdateStreamUUID', type: 'request' });

    backOff(
      () =>
        this.waitForStreamingInfo().then(() => {
          if (this.pendingStreamUUID !== newStreamUUID) {
            // eslint-disable-next-line no-console
            console.log(
              'stream URL received: expected ',
              newStreamUUID,
              ' got ',
              (this.streamingInfo || {}).cdnURL,
              ', DEPRECATED as of ',
              this.pendingStreamUUID
            );

            return Promise.resolve('deprecated stream UUID');
          }

          if (isValidStreamName(this.streamingInfo)) {
            return this.streamingInfo;
          }

          return Promise.reject(new Error('invalid stream'));

        }),
      { jitter: 'full', numOfAttempts: 100, maxDelay: 10000 }
    ).catch((err) => {
      this.messagingService.emit(HLS_STREAMING_URL_FAILED, err.message);
    });
  };

  @action setProfileName(username) {
    this.profileName = username;
  }

  @action setBroadcastStatus = (status) => {
    this.messagingService.emit(FIREBASE_DEBUG, {
      event: 'setBroadcastStatus',
      newStatus: status,
      oldStatus: this.broadcastStatus,
    });
    this.broadcastStatus = status;
  };

  @action toggleFullscreen = () => {
    this.fullscreen = !this.fullscreen;
  };

  @action togglePortraitTrueFullScreen = () => {
    this.portraitTrueFullScreen = !this.portraitTrueFullScreen;
    if (this.portraitTrueFullScreen) {
      this.fullscreen = false;
      ChatStore.resetChatShown();
    }
  };

  @action resetPortraitFullScreen = () => {
    this.portraitTrueFullScreen = false;
  };

  @action toggleDeviceInLandscape = (isLandscape) => {
    this.deviceInLandscape = isLandscape;
  };

  @action getStreamingInfo = (username = this.profileName) => {
    if (FirebaseStore.isStreamingSaas) {
      return;
    }

    this.messagingService.emit(HLS_DEBUG, { method: 'profileStore::getStreamingInfo', username });
    this.messagingService.emit(GET_STREAMING_INFO, username);
  };

  waitForStreamingInfo = (username = this.profileName) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'profileStore::waitForStreamingInfo', username });

    return this.messagingService.emitAndWait({
      event: {
        name: GET_STREAMING_INFO,
        args: username,
      },
      successEvent: GET_STREAMING_INFO_RESPONSE,
      errorEvent: GET_STREAMING_INFO_ERROR,
      timeout: 3000,
      predicate: (ctx) => ctx === username,
    });
  };

  @action getPerformerInfo = (username = this.profileName) => {
    this.profileName = username;
    this.performerInfoState = APIStates.PENDING;
    ProfileService.getProfileInfo(username)
      .then((response) => {
        runInAction(() => {
          this.performerInfoState = APIStates.LOADED;
          this.profileNoIndexFollow = response.noIndex;
          this.profileInfo = response;
          this.performerDetails = response;
          this.profileThumbnailUrl = response.profileImageUrl;
          this.performerOnline = response.online;
          this.isViewerBroadcast = response.viewerBroadcast;
        });
        this.messagingService.emit(PROFILE_CONTROLLED_FEATURES, response.controlledFeatures);
        ControlledFeatureService.setProfileControlledFeatures(response.controlledFeatures);
        if (response.online) {
          this.broadcastStatus = BROADCAST_STATUS.PLAYING;
          const playerBroadcastMode = UserStore.hasCF(allUserGroups.BROADCASTER_AS_VIEWER);
          const isPerfomerWatchingOwnProfile = UserStore.user.username?.toLowerCase() === username;
          if (
            UserStore.hasCF(allUserGroups.BROADCASTER_ONLINE_PROFILE_V2) &&
            (!isPerfomerWatchingOwnProfile || playerBroadcastMode)
          ) {
            FirebaseStore.initRoom(
              this.profileName,
              UserStore.user.username,
              UserStore.user.accessHash
            );
          }
        } else {
          this.broadcastStatus = BROADCAST_STATUS.DISCONNECTED;
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        if (err.message.includes(StatusCodes.NOT_FOUND)) {
          this.performerInfoState = APIStates.ERROR_404;
        } else if (err.message.includes(StatusCodes.UNAUTHORIZED)) {
          this.performerInfoState = APIStates.ERROR_401;
        } else if (err.message.includes(StatusCodes.FORBIDDEN)) {
          this.performerInfoState = APIStates.ERROR_403;
        } else {
          this.performerInfoState = APIStates.ERROR;
        }
      });
  };

  @computed get profileExists() {
    return this.performerInfoState === APIStates.LOADED;
  }

  @computed get hasProfileInfo() {
    return Object.keys(this.profileInfo).length > 0;
  }

  @action getLoggedInUserInfo = (username) => {
    ProfileService.getProfileInfo(username).then((response) => {
      this.loggedInUserInfo = response;
    });
  };

  @action disableSwipe = (isDisabled) => {
    this.swipeDisabled = isDisabled;
  };

  @action sendProfileFeedback = (username, data) => {
    ProfileService.sendProfileFeedback(username, data);
    this.profileFeedbackSent = true;
  };

  @action handleSendProfileFeedbackResponseStatus = (status) => {
    this.profileFeedbackResponseStatus = status;
  };

  // eslint-disable-next-line complexity
  @computed get performerAttributes() {
    const {
      gender,
      ethnicity,
      femaleType,
      maleType,
      transType,
      countryId,
      mainLanguage,
      secondaryLanguages,
      femaleBodyType,
      maleBodyType,
      bodyType,
      sexPreference,
      breastSize,
      penisSize,
      transPenisSize,
      penisType,
      maleRole,
      hairColor,
      bodyHair,
      transBodyType,
      transBreastSize,
      transPenisType,
    } = this.profileInfo;

    let genderLink = gender === GENDER.SHEMALE ? GENDER.TRANSGENDER : gender;
    if (gender === GENDER.UNKNOWN) {
      genderLink = GENDER.ALL;
    }
    let ethnicityLink = ethnicity;
    if (ethnicity) {
      if (ethnicity.toLowerCase() === 'caucasian') {
        ethnicityLink = 'white';
      }
      if (ethnicity.toLowerCase() === 'latino') {
        ethnicityLink = 'hispanic';
      }
      if (ethnicity.toLowerCase() === 'native_american') {
        ethnicityLink = 'native-american';
      }
    }
    let countryLink = '';
    if (countryId) {
      const country = AVAILABLE_COUNTRIES.find((countryEl) => countryEl.code === countryId);
      countryLink = formatCountryName(country.name);
    }
    const findLngName = (lngCode) => {
      const lngObj = AVAILABLE_LANGUAGES.find((lng) => lng.code === lngCode);

      return lngObj && lngObj.name.toLowerCase();
    };
    let mainLanguageLink = '';
    if (mainLanguage) {
      mainLanguageLink = findLngName(mainLanguage);
    }
    let secLng1 = '';
    let secLngLink1 = '';
    if (secondaryLanguages && secondaryLanguages.length && secondaryLanguages[0]) {
      secLng1 = secondaryLanguages[0];
      secLngLink1 = findLngName(secLng1);
    }
    let secLng2 = '';
    let secLngLink2 = '';
    if (secondaryLanguages && secondaryLanguages.length && secondaryLanguages[1]) {
      secLng2 = secondaryLanguages[1];
      secLngLink2 = findLngName(secLng2);
    }
    let bodyTypeLink = '';
    if (bodyType) {
      if (bodyType === 'slimPetite') {
        bodyTypeLink = 'slim-petite';
      } else if (bodyType === 'morethanAverage') {
        bodyTypeLink = 'more-than-average';
      } else {
        bodyTypeLink = bodyType;
      }
    }
    let hairColorLink = '';
    if (hairColor) {
      if (hairColor === 'bald') {
        hairColorLink = hairColor;
      } else {
        hairColorLink = `${hairColor}-hair`;
      }
    }
    let bodyHairLink = '';
    let bodyHairKey = 'profile.profile.bodyHair.';
    let bodyHairValue = bodyHair;
    if (bodyHairValue) {
      if (bodyHairValue === 'average') {
        bodyHairLink = `${bodyHairValue}-hair`;
        bodyHairKey = 'v2profile.attribute.';
        bodyHairValue = 'averageBodyHair';
      } else {
        bodyHairLink = bodyHairValue;
      }
    }
    let penisSizeValue = penisSize && penisSize.toLowerCase();
    let penisSizeKey = 'profile.profile.penisSize.';
    if (penisSizeValue === 'average') {
      penisSizeKey = 'v2profile.attribute.';
      penisSizeValue = 'averagePenis';
    }
    let transPenisSizeValue = transPenisSize && transPenisSize.toLowerCase();
    let transPenisSizeKey = 'profile.profile.transPenisSize.';
    if (transPenisSizeValue === 'average') {
      transPenisSizeKey = 'v2profile.attribute.';
      transPenisSizeValue = 'averagePenis';
    }
    let maleBodyTypeValue = maleBodyType && maleBodyType.toLowerCase();
    let maleBodyTypeKey = 'profile.profile.maleBodyType.';
    let maleBodyLink = `/${genderLink}/${maleBodyTypeValue}-${genderLink}-body`;
    if (maleBodyTypeValue === 'average') {
      maleBodyTypeKey = 'v2profile.attribute.';
      maleBodyTypeValue = 'averageBody';
      maleBodyLink = `/${genderLink}/average-body`;
    }
    let femaleBodyTypeValue = femaleBodyType && femaleBodyType.toLowerCase();
    let femaleBodyTypeKey = 'profile.profile.femaleBodyType.';
    const femaleBodyLink = `/${genderLink}/${femaleBodyTypeValue}-${genderLink}-body`;
    if (femaleBodyTypeValue === 'average') {
      femaleBodyTypeKey = 'v2profile.attribute.';
      femaleBodyTypeValue = 'averageBody';
    }
    let transBodyTypeValue = transBodyType && transBodyType.toLowerCase();
    let transBodyTypeKey = 'profile.profile.femaleBodyType.';
    const transBodyLink = `/${genderLink}/${transBodyTypeValue}-${genderLink}-body`;
    if (transBodyTypeValue === 'average') {
      transBodyTypeKey = 'v2profile.attribute.';
      transBodyTypeValue = 'averageBody';
    }
    let bodyTypeValue = bodyType && bodyType.toLowerCase();
    let bodyTypeKey = 'profile.profile.bodyType.';
    let bodyLink = `/${genderLink}/${bodyTypeLink}`;
    if (bodyTypeValue === 'average') {
      bodyTypeKey = 'v2profile.attribute.';
      bodyTypeValue = 'averageBody';
      bodyLink = `/${genderLink}/average-body`;
    }
    if (bodyTypeValue === 'slimpetite') {
      bodyTypeValue = 'slimPetite';
    }
    if (bodyTypeValue === 'morethanaverage') {
      bodyTypeValue = 'morethanAverage';
    }
    if (maleBodyTypeValue || femaleBodyTypeValue || transBodyTypeValue) {
      bodyTypeValue = null;
      bodyTypeKey = null;
      bodyLink = null;
    }

    return [
      {
        key: 'profile.profile.gender.',
        value: genderLink,
        link: `/${genderLink}`,
      },
      {
        key: 'v2profile.profileInfo.ethnicity.',
        value: ethnicity,
        link: `/${genderLink}/${ethnicityLink}`,
      },
      {
        key: 'profile.profile.femaleType.',
        value: femaleType && femaleType.toLowerCase(),
        link: `/${genderLink}/${femaleType}`,
      },
      {
        key: 'profile.profile.maleType.',
        value: maleType && maleType.toLowerCase(),
        link: `/${genderLink}/${maleType}-male-type`,
      },
      {
        key: 'profile.profile.transType.',
        value: transType && transType.toLowerCase(),
        link: `/${genderLink}/${transType && transType.replace('_', '-')}`,
      },
      {
        key: 'countries.',
        value: countryId,
        link: `/${genderLink}/${countryLink}`,
      },
      {
        key: 'languages.',
        value: mainLanguage,
        link: `/${genderLink}/${mainLanguageLink}`,
      },
      {
        key: 'languages.',
        value: secLng1,
        link: `/${genderLink}/${secLngLink1}`,
      },
      {
        key: 'languages.',
        value: secLng2,
        link: `/${genderLink}/${secLngLink2}`,
      },
      {
        key: femaleBodyTypeKey,
        value: femaleBodyTypeValue,
        link: femaleBodyLink,
      },
      {
        key: maleBodyTypeKey,
        value: maleBodyTypeValue,
        link: maleBodyLink,
      },
      {
        key: transBodyTypeKey,
        value: transBodyTypeValue,
        link: transBodyLink,
      },
      {
        key: bodyTypeKey,
        value: bodyTypeValue,
        link: bodyLink,
      },
      {
        key: 'profile.profile.sexPreference.',
        value: sexPreference,
        link: `/${genderLink}/${sexPreference}`,
      },
      {
        key: 'profile.profile.breastSize.',
        value: breastSize && breastSize.toLowerCase(),
        link: `/${genderLink}/${breastSize}-breasts`,
      },
      {
        key: 'profile.profile.breastSize.',
        value: transBreastSize && transBreastSize.toLowerCase(),
        link: `/${genderLink}/${transBreastSize}-breasts`,
      },
      {
        key: penisSizeKey,
        value: penisSizeValue,
        link: `/${genderLink}/${penisSize}-penis`,
      },
      {
        key: transPenisSizeKey,
        value: transPenisSizeValue,
        link: `/${genderLink}/${transPenisSize && transPenisSize.replace('_', '-')}-trans-penis`,
      },
      {
        key: 'profile.profile.penisType.',
        value: transPenisType && transPenisType.toLowerCase(),
        link: `/${genderLink}/${transPenisType}`,
      },
      {
        key: 'profile.profile.penisType.',
        value: penisType && penisType.toLowerCase(),
        link: `/${genderLink}/${penisType}`,
      },
      {
        key: 'profile.profile.maleRole.',
        value: maleRole && maleRole.toLowerCase(),
        link: `/${genderLink}/${maleRole}`,
      },
      {
        key: 'profile.profile.hairColor.',
        value: hairColor,
        link: `/${genderLink}/${hairColorLink}`,
      },
      {
        key: bodyHairKey,
        value: bodyHairValue,
        link: `/${genderLink}/${bodyHairLink}`,
      },
    ].filter((att) => !!att.value && att.value !== 'unknown' && att.link);
  }

  @action getPerformerGifts = (username = this.profileName) => {
    ProfileService.getPerformerGifts(username)
      .then(this._handlePerformerGiftsResponse)
      .catch((err) => {
        parseCatch(err);
      });
  };

  _handlePerformerGiftsResponse = (response) => {
    if (response.length === 0) {
      this.peformerGifts = response;
    } else {
      const transformResponse = response.map((gift) => {
        return {
          ...gift,
          id: uniqueId(),
        };
      });

      this.performerGifts = transformResponse;
    }
  };

  @computed get getFirstTenChunkedGifts() {
    return chunkArray(this.performerGifts.slice(0, 10), 5);
  }

  @action getPerformerFanClubInfo = (username = this.profileName) => {
    this.profileName = username;
    ProfileService.getPerformerFanClubInfo(username)
      .then((response) => {
        this.performerFanClubInfo = response;
      })
      .catch(() => {
        this.performerFanClubInfo = { activated: false };
      });
  };

  @action getPerformerFriendsStatus = (username) => {
    if (username === this.profileName) {
      return;
    }
    ProfileService.getPerformerFriendsStatus(username, this.profileName)
      .then((response) => {
        if (response && response.status) {
          this.performerFriendStatus = response.status;
        }
      })
      .catch((err) => {
        parseCatch(err);
      });
  };

  @action handleAddFriend = (username, profileName = this.profileName, status = this.performerFriendStatus) => {
    this.addFriendResponseStatus = APIStates.PENDING;
    ProfileService.handleFriendRequest(username, profileName, status)
      .then((response) => {
        this.messagingService.emit(TOGGLE_FRIENDS, { username, profileName, oldStatus: status });
        if (response && response.status === StatusCodes.OK) {
          const messageAction = {
            [ENUM_FRIEND_STATUS.NONE]: translate('v2broadcast.friend.request.sent'),
            [ENUM_FRIEND_STATUS.PENDING]: translate('v2broadcast.cancel.friend.request'),
            [ENUM_FRIEND_STATUS.ACCEPTED]: translate('v2broadcast.remove.friend'),
          };
          const successMessage = messageAction[status];
          this.addFriendResponseStatus = APIStates.LOADED;
          if (this.performerFriendStatus === ENUM_FRIEND_STATUS.NONE) {
            this.performerFriendStatus = ENUM_FRIEND_STATUS.PENDING;
          } else {
            this.performerFriendStatus = ENUM_FRIEND_STATUS.NONE;
          }
          if (successMessage) {
            NotificationService.create({
              type: NotificationService.type.TOAST,
              text: successMessage,
              subtype: NotificationService.subtype.SUCCESS,
            });
          }
        }
      })
      .catch((err) => {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('v2profile.notification.error.default'),
          subtype: NotificationService.subtype.ERROR,
        });
        this.addFriendResponseStatus = APIStates.ERROR;
        parseCatch(err);
      });
  };

  @action getPerformerFavouritesStatus = (username) => {
    this.isLoadingPerformerFavouriteStatus = APIStates.PENDING;

    if (username === this.profileName) {
      this.isLoadingPerformerFavouriteStatus = APIStates.LOADED;

      return;
    }
    ProfileService.getPerformerFavouritesStatus(username, this.profileName)
      .then((response) => {
        if (response && response.status) {
          this.performerFavouriteStatus = response.status;
        }
      })
      .catch((err) => {
        parseCatch(err);
      })
      .finally(() => {
        this.isLoadingPerformerFavouriteStatus = APIStates.LOADED;
      });
  };

  @action getPerformerFavouritesCount = () => {
    ProfileService.getPerformerFavouritesCount(this.profileName)
      .then((response) => {
        if (response) {
          this.favoritedCount = response.favoritedCount;
        }
      })
      .catch((err) => {
        parseCatch(err);
      });
  };

  @action getPerformerFriends = (username) => {
    this.messagingService.emit(GET_USER_FRIENDS, username);
  };

  @action handleToggleFavorite = (username, profileName = this.profileName, status = this.performerFavouriteStatus) => {
    this.addFavoriteResponseStatus = APIStates.PENDING;
    ProfileService.handleToggleFavorite(username, profileName, status)
      .then((response) => {
        this.messagingService.emit(TOGGLE_FAVORITES, { username, profileName, oldStatus: status });
        if (location.pathname !== routeDefinitions.broadcaster) {
          this.getPerformerFavouritesCount();
        }
        if (response && response.status === StatusCodes.OK) {
          const messageAction = {
            [ENUM_FAVOURITE_STATUS.NOT_FAVORITE]: translate('v2broadcast.added.to.favorites'),
            [ENUM_FAVOURITE_STATUS.FAVORITE]: translate('v2broadcast.removed.from.favorites'),
          };
          const successMessage = messageAction[status];
          this.addFavoriteResponseStatus = APIStates.LOADED;
          if (this.performerFavouriteStatus === ENUM_FAVOURITE_STATUS.NOT_FAVORITE) {
            this.performerFavouriteStatus = ENUM_FAVOURITE_STATUS.FAVORITE;
          } else {
            this.performerFavouriteStatus = ENUM_FAVOURITE_STATUS.NOT_FAVORITE;
          }
          if (successMessage) {
            NotificationService.create({
              type: NotificationService.type.TOAST,
              text: successMessage,
              subtype: NotificationService.subtype.SUCCESS,
            });
          }
        }
      })
      .catch((err) => {
        this.addFavoriteResponseStatus = APIStates.ERROR;
        parseCatch(err);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('v2profile.notification.error.default'),
          subtype: NotificationService.subtype.ERROR,
        });
      });
  };

  @action getSilenceList = (username) => {
    if (username) {
      this.messagingService.emit(GET_SILENCE_LIST, username);
    }
  };

  @action silenceOrBanOrKickUser = (
    isUserRoomModerator,
    silenceTarget,
    username,
    silenceOrUnsilence,
    accessHash,
    accessLevel,
    type,
    roomname
  ) => {
    const dataForRegularUser = {
      username,
      silenceTarget,
      silenceOrUnsilence,
    };
    const dataForModerator = {
      roomname,
      username,
      accessHash,
      accessLevel,
      type,
      recipient: silenceTarget,
    };
    this.messagingService.emit(
      isUserRoomModerator ? SILENCE_OR_BAN_USER_AS_MODERATOR : SILENCE_USER,
      isUserRoomModerator ? dataForModerator : dataForRegularUser
    );
  };

  @action featureBroadcaster = (roomname, username, accessHash, accessLevel, type, recipient, index, indexMinutes) => {
    const dataFeatureBroadcaster = {
      roomname: this.profileName.toLowerCase(),
      username,
      accessHash,
      accessLevel,
      type,
      recipient,
      index,
      indexMinutes,
    };
    this.messagingService.emit(PERFORMER_HAS_BEEN_FEATURED, dataFeatureBroadcaster);
  };

  @action closeRoom = (roomname, username, accessHash, accessLevel, type, recipient) => {
    const dataCloseRoom = {
      roomname: this.profileName.toLowerCase(),
      username,
      accessHash,
      accessLevel,
      type,
      recipient,
    };
    this.messagingService.emit(ROOM_HAS_BEEN_CLOSED, dataCloseRoom);
  };

  @action reportUser = (abuseType, abuseSubtype, comment, reportedUsername, broadcasterUsername) => {
    const data = {
      abuseType,
      abuseSubtype,
      comment,
      reportedUsername,
      broadcasterUsername,
    };
    if (abuseSubtype === '') {
      data.abuseSubtype = 'other';
    }
    this.messagingService.emit(REPORT_USER, data);
  };

  isUserSilenced = (username) =>
    computed(
      () =>
        this.silenceList.usersList &&
        this.silenceList.usersList.filter((silencedUser) => silencedUser.username === username).length > 0
    ).get();

  /**
   * Intitiating tipping flow. From this point on, the logic will be handled by the componentes or services themselves
   * Ex. It can trigger register/login/tipping modal
   *
   * @param {String} performer
   * @memberof ProfileStore
   */
  @action initiateTip(performer) {
    this.messagingService.emit(INITIATE_SEND_TIP, {
      performerUserName: performer,
      performerImage: this.performerDetails.profileImageUrl,
    });
  }

  @action.bound showRegistrationModal() {
    this.messagingService.emit(SHOW_REGISTRATION_MODAL, {});
  }

  @action.bound showUpgradeToGold(area, modalProps = {}) {
    ModalService.showModal(
      ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_CONTEXTUAL,
      { area, ...modalProps }
    );
  }

  @action _handleStreamingInfo = (streamInfo) => {
    if (!streamInfo) {
      return;
    }

    if (streamInfo && streamInfo.useStreamingSaas) {
      return;
    }

    const isFbAuthDataExists = !!this.fbAuthData;
    const isPrivateRoom = isFbAuthDataExists && this.fbAuthData.privateRoom;
    const isSpying = this.broadcastStatus === BROADCAST_STATUS.SPYING && this.playToken;
    const insidePSOrGS =
      (this.broadcastStatus === BROADCAST_STATUS.ACCEPTED_INSIDE_PS ||
        this.broadcastStatus === BROADCAST_STATUS.INSIDE_GS) &&
      !!this.playToken;

    const shouldUseEdgeURL = insidePSOrGS || isSpying || isPrivateRoom;

    if (!this.fbAuthData || (shouldUseEdgeURL && !streamInfo.edgeURL)) {
      const delay = 1000;
      setTimeout(() => {
        ProfileService.getStreamingInfo(this.profileName);
      }, delay);

      return;
    }

    if (!streamInfo.edgeURL) {
      delete streamInfo.edgeURL;
    }
    if (!streamInfo.cdnURL) {
      delete streamInfo.cdnURL;
    }
    this.streamingInfo = { ...streamInfo };

    this.messagingService.emit(HLS_DEBUG, { method: 'profileStore::_handleStreamingInfo', streamInfo });
    this.handleMobileLoadingState(false);
  };

  @action handleMobileLoadingState = (isMobileLoadingVisible) => {
    this.isMobileLoadingVisible = isMobileLoadingVisible;
  };

  @action getSimilarCams = (username, genderPreference) => {
    const performerName = this.profileName;
    ProfileService.getSimilarCams(username, performerName, genderPreference);
  };

  @action loadConversations = (data, messages) => {
    const DELAY = 2000;
    const recipient = data.username?.split(',')[1];
    this.privateMessageSendingStatus = APIStates.PENDING;
    messages.forEach((messageItem, index) => {
      const { message, mType } = messageItem;
      ProfileService.loadConversations(data).then((response) => {
        if (response?.status === REQUEST_STATUS.SUCCESS) {
          const threadId = response.thread_id;
          if (threadId <= 0) {
            // add error handling for banned user
            this.privateMessageSendingStatus = APIStates.ERROR;
            NotificationService.create({
              type: NotificationService.type.TOAST,
              text: translate('v2profile.notification.error.default'),
              subtype: NotificationService.subtype.ERROR,
            });
            // redirect to directory if recipient is banned
            if (recipient && window.location.href.includes(recipient)) {
              setTimeout(() => {
                window.location = routeDefinitions.root;
              }, DELAY);
            }

            return;
          }
          const timestamp = new Date().getTime();
          const privateMessageData = {
            threadId,
            message,
            mType,
            id: timestamp,
          };
          ProfileService.postPrivateMessage(privateMessageData).then((privateResponse) => {
            if (privateResponse?.status === REQUEST_STATUS.SUCCESS) {
              if (messages.length - 1 === index) {
                this.privateMessageSendingStatus = APIStates.LOADED;
                this.messagingService.emit(PROFILE_PRIVATE_MESSAGE_SENT);
                NotificationService.create({
                  type: NotificationService.type.TOAST,
                  text: translate('profile.profile.message.sent'),
                  subtype: NotificationService.subtype.SUCCESS,
                });
              }
            } else {
              this.privateMessageSendingStatus = APIStates.ERROR;
              NotificationService.create({
                type: NotificationService.type.TOAST,
                text: translate('v2profile.notification.error.default'),
                subtype: NotificationService.subtype.ERROR,
              });
            }
          });
        } else {
          this.privateMessageSendingStatus = APIStates.ERROR;
          NotificationService.create({
            type: NotificationService.type.TOAST,
            text: translate('v2profile.notification.error.default'),
            subtype: NotificationService.subtype.ERROR,
          });
        }
      });
    });
  };

  @action getUploadUrl(file, user) {
    const fileName = file.name;
    const extension = getExtension(fileName);

    const data = {
      size: file.size,
      contentType: file.type,
      extension,
      username: user.username,
      accessHash: user.accessHash,
    };
    ProfileService.getUploadUrl(data).then((response) => {
      const { tmpUploadUrl } = response;
      const { accessUrl } = response;
      ProfileService.putFileToCDN(tmpUploadUrl, file).then(() => {
        this.messagingService.emit(SEND_ACCESS_FILE_URL, accessUrl);
      }).catch((error) => {
        this.messagingService.emit(SEND_ACCESS_FILE_URL, null, error);
      });
    }).catch((error) => {
      if (error.message === ERRORS.REQUEST_TIME_OUT) {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.ERROR,
          text: translate('v2profile.privateMessage.fileUploadError'),
        });
        this.messagingService.emit(SEND_ACCESS_FILE_URL, null, error);
      }
    });
  }

  @action getWishList = (username) => {
    this.messagingService.emit(WISHLIST_INFO, username);
  };

  @action initiatePrivateMessage(data) {
    this.messagingService.emit(INITIATE_PRIVATE_MESSAGE, data);
  }

  @action getSexQuestionnaire() {
    this.messagingService.emit(GET_SEX_QUESTIONNAIRE, this.profileName);
  }

  _handleSilenceList = (silenceList) => {
    this.silenceList = silenceList;
  };

  @action _handleFbAuthData = (fbAuthData) => {
    this.fbAuthData = fbAuthData;
    if (FirebaseStore.isStreamingSaas) {
      this.handleMobileLoadingState(false);
    }
  };

  _handleGetUserData = (userData) => {
    this.currentUser = userData;
  };

  _handleWishList = (wishList) => {
    this.wishList = wishList;
  };

  _handleFriendsList = (friends) => {
    this.friendsCount = friends.totalUsersCount;
  };

  _trackPrivateShowReq = () => {
    if (FirebaseStore.isPrivateShowRequestActive) {
      TrackingService.track({
        event: 'Private Show',
        eventAction: 'Request Private Show',
        eventValue: `${this.profileName}:${this.currentUser.username}`,
      });
    }
  };

  @computed get isWishListVisible() {
    return Object.values(this.wishList).some((wish) => !!wish);
  }

  @action getReportUserInfo = (broadcasterUsername, reportedUsername) => {
    const data = {
      broadcasterUsername,
      reportedUsername,
    };
    this.messagingService.emit(GET_REPORT_USER_INFO, data);
  };

  @action setStreamHasEndedState = (value) => {
    this.streamHasEndedState = value;
  };

  @action setPrivateShowReady = (value) => {
    this.privateShowReady = value;
  };

  @action setPrivateShowEnded = (value) => {
    this.privateShowEnded = value;
  };

  @action setPrivateShowRequested = (value) => {
    this.privateShowRequested = value;
  };

  @action setAdminSpyShowRequested = (value, time) => {
    this.adminSpyShowRequested = value;
    this.adminSpyShowTime = time;
  };

  @action setSpyShowRequested = (value) => {
    this.spyShowRequested = value;
  };

  @action setSpyRequestInProgress = (value) => {
    this.spyRequestInProgress = value;
  };

  @action setSpyShowEnded = (value) => {
    this.spyShowEnded = value;
  };

  @action setPublicShowEnded = (value) => {
    this.publicShowEnded = value;
  };

  @action setSpyShowStartTime = (value) => {
    this.spyShowStartTime = value;
  };

  @action setPrivateShowStartTime = (value) => {
    this.privateShowStartTime = value;
  };

  @action setFeedbackImpresion = (feedbackImpression) => {
    this.feedbackImpression = feedbackImpression;
  };

  _handleReportInfoResponse = (isUserReported) => {
    this.isUserReported = isUserReported;
  };

  @action setPlayToken = (token) => {
    this.messagingService.emit(FIREBASE_DEBUG, { event: 'setPlayToken', tokenEmpty: !token });
    this.playToken = token;
  };

  @action setPrivateHls = (hls) => {
    this.privateHls = hls;
  };

  @action setPrivateStreamingSources = (value) => {
    this.privateStreamingSources = value;
  };

  @action _handlePlayToken = (response) => {
    if (response) {
      this.messagingService.emit(FIREBASE_DEBUG, {
        event: '_handlePlayToken',
        status: response.status,
        message: response.message,
      });
    } else {
      this.messagingService.emit(FIREBASE_DEBUG, { event: '_handlePlayToken', status: 'empty response' });
    }
    if (response && response.status === 'success') {
      if (FirebaseStore.isStreamingSaas && response.output) {
        this.setPrivateStreamingSources(response);
      } else {
        this.playToken = response.playToken;
        this.privateHls = response.hls;
      }
      this.setPsGsLoading(APIStates.LOADED);
    } else if (response && response.status === 'notInTheRoom') {
      // if (FirebaseStore.isStreamingSaas) this is also needed for wowza
      const user = UserStore.user || {};
      const roomName = this.profileName;
      const tokenPayload = { roomname: roomName, username: user.username, accessHash: user.accessHash };
      const GET_TOKEN_DELAY = 1000;
      setTimeout(() => {
        this.messagingService.emit(FIREBASE_DEBUG, { event: '_handlePlayToken-retry', reason: 'notInTheRoom' });
        this.messagingService.emit(GET_PLAY_TOKEN_FOR_GROUP_SHOW, tokenPayload);
      }, GET_TOKEN_DELAY);
    } else if (response && response.status === 'failed') {
      if (response.message === 'PRIVATE_SHOW_TIMEOUT_PENALTY') {
        ModalService.showModal(ModalService.MODAL_KEYS.GROUP_SHOW_CANCELED_MODAL, { isSpyShow: true });
        this.messagingService.emit(REJOIN_SPY_SHOW_ERROR);
      }
      if (response.message === 'PRIVATE_SHOW_VIEWER_NOT_IN_ROOM') {
        this.messagingService.emit(REJOIN_SPY_SHOW_ERROR);
      }
    }
  };

  _handleStopPS = (response) => {
    if (response && response.status === 'success') {
      this.stopPS();
    }
  };

  stopPS = () => {
    if (!FirebaseStore.isStreamingSaas) {
      this.playToken = '';
    }
    this.privateShowRequested = false;
    FirebaseStore.togglePrivateShowRequest(false);
    TrackingService.track({
      event: 'Private Show',
      eventAction: 'End Private Show',
      eventValue: `${this.profileName}:${this.currentUser.username}`,
    });
  };

  _handleStopSpy = (response) => {
    if (response && response.status === 'success') {
      if (!FirebaseStore.isStreamingSaas) {
        this.playToken = '';
      }
      this.spyShowRequested = false;
      this.spyShowEnded = true;
    }
  };

  _handleUserBroadcastStarted = () => {
    this.getPerformerInfo();
    this.getStreamingInfo();
    this.setBroadcastingStarted(true);
    this.setPerformerOnline(true);
    this.setBroadcastStatus(BROADCAST_STATUS.PLAYING);
    this.messagingService.emit(REINIT_ROOM, {
      roomName: this.profileName,
      username: UserStore.user.username,
      accessHash: UserStore.user.accessHash,
    });
  };

  @action
    _handleInitialFirebaseState = (roomState) => {
      if (FirebaseStore.isStreamingSaas) {
        this.messagingService.emit(FIREBASE_DEBUG, { event: '_handleInitialFirebaseState', roomState });
        if (
          roomState.newShowsState === NEW_SHOWS_STATE.PAUSED_GS ||
        roomState.newShowsState === NEW_SHOWS_STATE.INSIDE_GS ||
        roomState.newShowsState === NEW_SHOWS_STATE.BONUS_GS
        ) {
          this.setBroadcastStatus(BROADCAST_STATUS.INSIDE_GS);
        }
      // TODO set proper state here and remove all calls to setBroadcastStatus form UI components!!!
      } else {
        this.getStreamingInfo(this.profileName);
      }
    };

  _handleSexQuestionnaireResponse = (sexQuestionnaires) => {
    this.sexQuestionnaires = sexQuestionnaires;
  };

  @computed get hasSexQuestionnaires() {
    return this.sexQuestionnaires && this.sexQuestionnaires.some((sq) => sq.answer && sq.answer.length);
  }

  _handleReportUserResponseStatus = (reportUserResponseStatus) => {
    this.reportUserResponseStatus = reportUserResponseStatus;
  };

  _handleSilenceUserResponseStatus = ({ status, username }) => {
    this.silenceUserResponseStatus = status;

    // when silence request is resolved call list to update UI
    if (status === APIStates.LOADED) {
      this.getSilenceList(username);
    }
  };

  _handleAwardsResponse = (awardsResponse) => {
    this.awards = awardsResponse;
  };

  @action resetChatStoreState = () => {
    Object.keys(profileStoreInitialState).forEach((item) => (this[item] = profileStoreInitialState[item]));
  };

  @action getAwards = (performerName) => {
    AwardService.getAwards(performerName);
  };

  @action resetReportResponseStatus = () => {
    this.reportUserResponseStatus = APIStates.INITIAL;
    this.handleReportInfoStatus(APIStates.INITIAL);
  };

  @action handleFeaturedCamStats = (featuredCamStats) => {
    this.featuredCamStats = featuredCamStats;
  };

  @action toggleHideProfileStream = () => {
    this.hideProfileStream = !this.hideProfileStream;
  };

  getFeaturedCamStats = (username) => {
    ProfileService.getFeaturedCamStats(username);
  };

  @computed get hasMaxAttributes() {
    const ATTRIBUTES_MAX_NUM = 5;

    return this.performerAttributes.length >= ATTRIBUTES_MAX_NUM;
  }

  @computed get isLoadingFriendsButton() {
    return this.addFriendResponseStatus === APIStates.PENDING;
  }

  @computed get isLoadingFavoritesButton() {
    return this.addFavoriteResponseStatus === APIStates.PENDING;
  }

  @computed get userFavoriteSuccess() {
    return this.addFavoriteResponseStatus === APIStates.LOADED;
  }

  @computed get isLoadingReport() {
    return this.reportUserResponseStatus === APIStates.PENDING;
  }

  @computed get isLoadingSilence() {
    return this.silenceUserResponseStatus === APIStates.PENDING;
  }

  @computed get isAwardAreaVisible() {
    return this.awards.length > 0;
  }

  @computed get defaultAwardsState() {
    return this.awards.slice(0, 4);
  }

  @computed get isPerformerFavorited() {
    return this.performerFavouriteStatus === ENUM_FAVOURITE_STATUS.FAVORITE;
  }

  @computed get isUserInPaidShow() {
    return (
      this.broadcastStatus === BROADCAST_STATUS.ACCEPTED_INSIDE_PS ||
      this.broadcastStatus === BROADCAST_STATUS.INSIDE_GS ||
      this.broadcastStatus === BROADCAST_STATUS.SPYING
    );
  }

  @computed get silencedListUsernames() {
    return this.silenceList.totalUsersCount > 0 ? this.silenceList.usersList.map((user) => user.username) : [];
  }

  @computed get isPlayerBroadcastMode() {
    const { user } = UserStore;
    const hasPlayerBroadcastCF = UserStore.hasCF(allUserGroups.BROADCASTER_AS_VIEWER);
    const isMyProfile = user?.username?.toLowerCase() === this.profileName?.toLowerCase();

    return hasPlayerBroadcastCF && isMyProfile && this.performerOnline;
  }

  @computed get hasNewFanClub() {
    const { fanClubMigrationStatus, temptedFanClub } = this.profileInfo || {};

    const isMigrated = fanClubMigrationStatus === 'MIGRATED';
    const hasTemptedFanClub = Boolean(temptedFanClub) && fanClubMigrationStatus !== 'NOT_MIGRATED';

    return hasTemptedFanClub || isMigrated;
  }
}

export default new ProfileStore();
